import { useWidget } from '@telescope/cassini-hooks';
import { Box, Button, FormControl, FormErrorMessage, Heading } from '@telescope/cassini-ui';
import { GenericObject } from '@telescope/cassini-utilities';
import { Open } from 'generated-types/widget';
import React, { useState } from 'react';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router-dom';
import reactStringReplace from 'react-string-replace';
import { ErrorType } from 'types';
import { ROUTES } from 'utils';
import { useAuth } from '../lib';
import { AuthMethod } from '../utils';
import { RegisterFormStep1 } from './RegisterFormStep1';
import { RegisterFormStep2 } from './RegisterFormStep2';

export const Register = () => {
  const { registerWithEmail, registerWithFacebook } = useAuth();
  const { data } = useWidget<Open>({ select: (data) => data.widget.globalViews.auth });
  const content = data.content.registrationModal;
  const [currentStep, setCurrentStep] = useState(1);
  const [formError, setFormError] = useState<React.ReactNode>(null);
  const [defaultValues, setDefaultValues] = useState();
  const navigate = useNavigate();
  const { data: errorCopy } = useWidget<Open>({ select: (data) => data.widget.globalViews.errors });

  const invalidEmailError = reactStringReplace(content.errorMessage.message, '{{LINK}}', (match, i) => (
    <>
      <Button
        fontSize="inherit"
        variant="link"
        onClick={() => navigate(ROUTES.LOGIN, { replace: true, state: { backgroundLocation: ROUTES.HOME } })}>
        {content.errorMessage.linkText}
      </Button>
    </>
  ));

  const handleRegisterStep1 = async (formData: any) => {
    // TODO: This logic is really weird and not intuitive. Figure out a way to improve this. It's weird that we have to do a login call on the register method, and that the login call is expected to throw an error.

    try {
      let res;
      if (formData.method === AuthMethod.EMAIL) {
        res = await registerWithEmail({ payload: formData, checkIfUserAlreadyRegistered: true });
      }
      if (formData.method === AuthMethod.FACEBOOK) {
        res = await registerWithFacebook({ payload: formData, checkIfUserAlreadyRegistered: true });
      }

      // User is registered already, Prompt to login
      if (res?.isUserAlreadyRegistered) {
        setFormError(invalidEmailError);
        return;
      }

      // User is not registered yet, go to next step
      setCurrentStep(2);
      setFormError(null);
      setDefaultValues({ ...formData, ...res?.user });
    } catch (err: any) {
      if (err.message === ErrorType.FB_PERMISSIONS) {
        return setFormError(errorCopy.content.fbPermissions.content.description);
      }

      setFormError(errorCopy.content.generic.content.description);
    }
  };

  const handleRegisterStep2 = async (formData: GenericObject) => {
    try {
      if (formData.method === AuthMethod.EMAIL) {
        await registerWithEmail({ payload: formData, checkIfUserAlreadyRegistered: false });
      }

      if (formData.method === AuthMethod.FACEBOOK) {
        await registerWithFacebook({ payload: formData, checkIfUserAlreadyRegistered: false });
      }
      TagManager.dataLayer({ dataLayer: { event: 'sign_up', method: formData.method } });

      navigate(ROUTES.HOME, { replace: true });
    } catch (err) {
      setFormError(errorCopy.content.generic.content.description);
    }
  };

  return (
    <Box>
      <Heading as="h1" textAlign="center">
        {content.headline}
      </Heading>

      {currentStep === 1 && <RegisterFormStep1 onFormSubmit={handleRegisterStep1} />}
      {currentStep === 2 && <RegisterFormStep2 onFormSubmit={handleRegisterStep2} defaultValues={defaultValues} />}

      <Button
        size="sm"
        type="button"
        variant="link"
        onClick={() => navigate(ROUTES.LOGIN, { replace: true, state: { backgroundLocation: ROUTES.HOME } })}>
        {content.loginLink}
      </Button>
      <FormControl isInvalid={!!formError}>
        <FormErrorMessage display="inline-block">{formError}</FormErrorMessage>
      </FormControl>
    </Box>
  );
};
